<!--
Copyright 2024 Carnegie Mellon University. All Rights Reserved.
Released under a MIT (SEI)-style license. See LICENSE.md in the
project root for license information.
-->
<!-- view only display -->
@if (showValueOnly && !canEdit && !canApprove && !isOwner) {
<span>
  <!-- dataType.DateTime -->
  @if (dataField.dataType === dataType.DateTime) {
  <span>
    @if (value) {
    {{ value | date: 'dd MMM yyyy HH:mm' }} {{ getTimezoneAbbr() }}
    }
  </span>
  }
  <!-- dataType.Html -->
  @if (dataField.dataType === dataType.Html) {
  <span>
    <angular-editor [innerHtml]="value" [config]="viewConfig"></angular-editor>
  </span>
  }
  <!-- dataType.Card -->
  @if (dataField.dataType === dataType.Card) {
  <span>
    {{ getCardName(value) }}
  </span>
  }
  <!-- dataType.Move -->
  @if (dataField.dataType === dataType.Move) {
  <span>
    {{ value }} - {{ getMoveTitle(value) }}
  </span>
  }
  <!-- dataType.User -->
  @if (dataField.dataType === dataType.User) {
  <span>
    {{ getUserName(value) }}
  </span>
  }
  <!-- dataType.Boolean or dataType.Checkbox -->
  @if (dataField.dataType === dataType.Boolean || dataField.dataType === dataType.Checkbox) {
  <span>
    <mat-checkbox [checked]="value" [disabled]="true">
    </mat-checkbox><br />&nbsp;
  </span>
  }
  <!-- dataType.Competency -->
  @if (dataField.dataType === dataType.Competency) {
  <span>
    @for (id of selectedCompetencies; track id; let isLast = $last) {
      <span [matTooltip]="getCompetencyTooltip(id)" matTooltipPosition="above" style="white-space: nowrap;">{{ displayCompetency(id) }}</span>@if (!isLast) {<span>, </span>}
    }
  </span>
  }
  <!-- other dataTypes -->
  @if (dataField.dataType !== dataType.DateTime && dataField.dataType !== dataType.Html && dataField.dataType !==
  dataType.Card && dataField.dataType !== dataType.Move && dataField.dataType !== dataType.User && dataField.dataType
  !== dataType.Boolean && dataField.dataType !== dataType.Checkbox && dataField.dataType !== dataType.Competency) {
  <span>
    @if (dataField.isMultiSelect) {
    @for (val of getValueArray(); track val) {
    {{ val }}<br>
    }
    } @else {
    {{ value }}
    }
  </span>
  }
</span>
} @else {
<!-- string input -->
@if (!dataField.isChosenFromList && (dataField.dataType === dataType.String || dataField.dataType === dataType.Url)) {
<span>
  @if (!showValueOnly) {
  <mat-form-field class="full-width">
    <mat-label>{{ dataField.name }}</mat-label>
    <input type="text" matInput placeholder="{{ dataField.name }}" [(ngModel)]="value"
      (ngModelChange)="valueChangeHandler()" [disabled]="!canEdit" />
  </mat-form-field>
  }
  @if (showValueOnly) {
  <span>
    {{ value }}
  </span>
  }
</span>
}
<!-- Numeric input -->
@if (!dataField.isChosenFromList && dataField.dataType === dataType.Integer || dataField.dataType === dataType.Double) {
<span>
  @if (!showValueOnly) {
  <mat-form-field class="full-width">
    <mat-label>{{ dataField.name }}</mat-label>
    <input matInput placeholder="{{ dataField.name }}" (change)="verifyNumber(value)" [(ngModel)]="value"
      (ngModelChange)="valueChangeHandler()" [disabled]="!canEdit" />
  </mat-form-field>
  }
  @if (showValueOnly) {
  <span>
    {{ value }}
  </span>
  }
</span>
}
<!-- dateTime input -->
@if (!dataField.isChosenFromList && dataField.dataType === dataType.DateTime) {
<span>
  @if (!showValueOnly) {
  <mat-form-field class="datetime-field">
    <mat-label>{{ dataField.name }}</mat-label>
    <input matInput [ngxMatDatetimePicker]="picker" [(ngModel)]="value" (ngModelChange)="valueChangeHandler()"
      (dateChange)="isChanged = true" (dateInput)="isChanged = true" style="display: none;" [disabled]="!canEdit">
    <!-- <datetime-picker> -->
    <span (click)="canEdit && picker.open()">
      @if (value) {
      {{ value | date: 'dd MMM yyyy HH:mm' }} {{ getTimezoneAbbr() }}
      }
    </span>
    <mat-datepicker-toggle matSuffix title="Choose Date/Time" [for]="picker" [disabled]="!canEdit">
    </mat-datepicker-toggle>
    <ngx-mat-datetime-picker #picker [showSpinners]=true [showSeconds]=false [stepHour]=1 [stepMinute]=5 [stepSecond]=1
      [touchUi]=false [enableMeridian]=true [disableMinute]=false [hideTime]=false [color]="'warn'">
      <ngx-mat-datepicker-actions>
        <button mat-stroked-button ngxMatDatepickerApply>Save</button>
        <button mat-stroked-button ngxMatDatepickerCancel>Cancel</button>
      </ngx-mat-datepicker-actions>
    </ngx-mat-datetime-picker>
  </mat-form-field>
  }
  @if (showValueOnly) {
  <span>
    @if (value) {
    {{ value | date: 'dd MMM yyyy HH:mm' }} {{ getTimezoneAbbr() }}
    }
  </span>
  }
</span>
}
<!-- html input -->
@if (!dataField.isChosenFromList && dataField.dataType === dataType.Html) {
<div>
  @if (!showValueOnly) {
  <div class="full-width">
    <div class="editor-label">
      {{ dataField.name }}
    </div>
    <div>
      <angular-editor [placeholder]="'Enter value ...'" [(ngModel)]="value" (ngModelChange)="valueChangeHandler()"
        [config]="canEdit ? editorConfig : viewConfig"></angular-editor>
    </div>
    &nbsp;
  </div>
  }
  @if (showValueOnly) {
  <div class="full-width">
    <angular-editor [innerHtml]="value" [config]="viewConfig"></angular-editor>
  </div>
  }
</div>
}
<!-- Organization input -->
@if (!dataField.isChosenFromList && dataField.dataType === dataType.Organization) {
<span>
  @if (!showValueOnly) {
  <mat-form-field class="full-width">
    <mat-label>{{ dataField.name }}</mat-label>
    <mat-select placeholder="{{ dataField.name }}" [(ngModel)]="value" (ngModelChange)="valueChangeHandler()"
      [disabled]="!canEdit">
      @if (organizationOptions && organizationOptions.length > 0) {
      <mat-option value="None">None</mat-option>
      }
      @for (organization of organizationOptions; track $index) {
      <mat-option [value]="organization">{{ organization
        }}</mat-option>
      }
    </mat-select>
  </mat-form-field>
  }
  @if (showValueOnly) {
  <mat-select placeholder="{{ dataField.name }}" [(ngModel)]="value" (ngModelChange)="valueChangeHandler()"
    [disabled]="!canEdit">
    @if (organizationOptions && organizationOptions.length > 0) {
    <mat-option value="None">None</mat-option>
    }
    @for (organization of organizationOptions; track $index) {
    <mat-option [value]="organization">{{ organization
      }}</mat-option>
    }
  </mat-select>
  }
</span>
}
<!-- TeamsMultiple value -->
@if (dataField.dataType === dataType.TeamsMultiple) {
<span>
  @if (!showValueOnly) {
  <mat-form-field class="full-width">
    <mat-label>{{ dataField.name }}</mat-label>
    <mat-select multiple autoclose="" placeholder="{{ dataField.name }}" (click)="$event.stopPropagation()"
      (valueChange)="setDataValueArray($event)" [value]="getValueArray()" [disabled]="!canEdit">
      @if (teamOptions && teamOptions.length > 0) {
      <mat-option value="ALL">ALL</mat-option>
      }
      @if (teamOptions && teamOptions.length > 0) {
      <mat-option value="None">None</mat-option>
      }
      @for (team of teamOptions; track team.id) {
      <mat-option [value]="team.shortName">
        {{ team.shortName }}
      </mat-option>
      }
    </mat-select>
  </mat-form-field>
  }
  @if (showValueOnly) {
  <mat-select multiple autoclose="" placeholder="{{ dataField.name }}" (click)="$event.stopPropagation()"
    (valueChange)="setDataValueArray($event)" [value]="getValueArray()" [disabled]="!canEdit">
    @if (teamOptions && teamOptions.length > 0) {
    <mat-option value="ALL">ALL</mat-option>
    }
    @if (teamOptions && teamOptions.length > 0) {
    <mat-option value="None">None</mat-option>
    }
    @for (team of teamOptions; track team.id) {
    <mat-option [value]="team.shortName">
      {{ team.shortName }}
    </mat-option>
    }
  </mat-select>
  }
</span>
}
<!-- Status value -->
@if (!dataField.isChosenFromList && dataField.dataType === dataType.Status) {
<span>
  @if (!showValueOnly) {
  <mat-form-field class="full-width">
    <mat-label>{{ dataField.name }}</mat-label>
    <mat-select placeholder="{{ dataField.name }}" [(ngModel)]="value" [disabled]="!canApprove"
      (ngModelChange)="valueChangeHandler()">
      @for (status of itemStatus; track $index) {
      <mat-option [value]="status">
        {{ status }}
      </mat-option>
      }
    </mat-select>
  </mat-form-field>
  }
  @if (showValueOnly) {
  <mat-select placeholder="{{ dataField.name }}" [(ngModel)]="value" [disabled]="!canApprove"
    (ngModelChange)="valueChangeHandler()">
    @for (status of itemStatus; track $index) {
    <mat-option [value]="status">
      {{ status }}
    </mat-option>
    }
  </mat-select>
  }
</span>
}
<!-- Assigned Unit value -->
@if (!dataField.isChosenFromList && dataField.dataType === dataType.Team) {
<span>
  @if (!showValueOnly) {
  <mat-form-field class="full-width">
    <mat-label>{{ dataField.name }}</mat-label>
    <mat-select placeholder="{{ dataField.name }}" [(ngModel)]="value" [disabled]="!isOwner"
      (ngModelChange)="valueChangeHandler()">
      @for (unit of unitOptions; track unit.id) {
      <mat-option [value]="unit.shortName">
        {{ unit.shortName }}
      </mat-option>
      }
    </mat-select>
  </mat-form-field>
  }
  @if (showValueOnly) {
  <mat-select placeholder="{{ dataField.name }}" [(ngModel)]="value" [disabled]="!isOwner"
    (ngModelChange)="valueChangeHandler()">
    @for (unit of unitOptions; track unit.id) {
    <mat-option [value]="unit.shortName">
      {{ unit.shortName }}
    </mat-option>
    }
  </mat-select>
  }
</span>
}
<!-- Card input -->
@if (!dataField.isChosenFromList && dataField.dataType === dataType.Card) {
<span>
  @if (!showValueOnly) {
  <mat-form-field class="full-width">
    <mat-label>{{ dataField.name }}</mat-label>
    <mat-select placeholder="{{ dataField.name }}" [(ngModel)]="value" (ngModelChange)="valueChangeHandler()"
      [disabled]="!canEdit">
      <mat-option value="''"> - - - </mat-option>
      @for (card of cardOptions; track card.id) {
      <mat-option [value]="card.id">{{ card.name }}</mat-option>
      }
    </mat-select>
  </mat-form-field>
  }
  @if (showValueOnly) {
  <mat-select placeholder="{{ dataField.name }}" [(ngModel)]="value" (ngModelChange)="valueChangeHandler()"
    [disabled]="!canEdit">
    <mat-option value="''"> - - - </mat-option>
    @for (card of cardOptions; track card.id) {
    <mat-option [value]="card.id">{{ card.name }}</mat-option>
    }
  </mat-select>
  }
</span>
}
<!-- Move input -->
@if (!dataField.isChosenFromList && dataField.dataType === dataType.Move) {
<span>
  @if (!showValueOnly) {
  <mat-form-field class="full-width">
    <mat-label>{{ dataField.name }}</mat-label>
    <mat-select placeholder="{{ dataField.name }}" [(ngModel)]="value" (ngModelChange)="valueChangeHandler()"
      [disabled]="!canEdit">
      <mat-option value="''"> - - - </mat-option>
      @for (move of moveOptions; track move.id) {
      <mat-option [value]="move.id">{{ move.moveNumber }} - {{ move.title
        }}</mat-option>
      }
    </mat-select>
  </mat-form-field>
  }
  @if (showValueOnly) {
  <mat-select placeholder="{{ dataField.name }}" [(ngModel)]="value" (ngModelChange)="valueChangeHandler()"
    [disabled]="!canEdit">
    <mat-option value="''"> - - - </mat-option>
    @for (move of moveOptions; track move.id) {
    <mat-option [value]="move.id">{{ move.moveNumber }} - {{ move.title
      }}</mat-option>
    }
  </mat-select>
  }
</span>
}
<!-- SourceType input -->
@if (!dataField.isChosenFromList && dataField.dataType === dataType.SourceType) {
<span>
  @if (!showValueOnly) {
  <mat-form-field class="full-width">
    <mat-label>{{ dataField.name }}</mat-label>
    <mat-select placeholder="{{ dataField.name }}" [(ngModel)]="value" (ngModelChange)="valueChangeHandler()"
      [disabled]="!canEdit">
      <mat-option value="''"> </mat-option>
      @for (sourceType of gallerySourceTypeOptions; track $index) {
      <mat-option [value]="sourceType">{{ sourceType
        }}</mat-option>
      }
    </mat-select>
  </mat-form-field>
  }
  @if (showValueOnly) {
  <mat-select placeholder="{{ dataField.name }}" [(ngModel)]="value" (ngModelChange)="valueChangeHandler()"
    [disabled]="!canEdit">
    <mat-option value="''"> </mat-option>
    @for (sourceType of gallerySourceTypeOptions; track $index) {
    <mat-option [value]="sourceType">{{ sourceType }}</mat-option>
    }
  </mat-select>
  }
</span>
}
<!-- User value -->
@if (dataField.dataType === dataType.User) {
<span>
  @if (!showValueOnly) {
  <mat-form-field class="full-width">
    <mat-label>{{ dataField.name }}</mat-label>
    <mat-select placeholder="{{ dataField.name }}" [(ngModel)]="value" (ngModelChange)="valueChangeHandler()"
      [disabled]="!canEdit">
      @for (user of userOptions; track user.id) {
      <mat-option [value]="user.id">
        {{ user.name }}
      </mat-option>
      }
    </mat-select>
  </mat-form-field>
  }
  @if (showValueOnly) {
  <mat-select placeholder="{{ dataField.name }}" [(ngModel)]="value" (ngModelChange)="valueChangeHandler()"
    [disabled]="!canEdit">
    @for (user of userOptions; track user.id) {
    <mat-option [value]="user.id">
      {{ user.name }}
    </mat-option>
    }
  </mat-select>
  }
</span>
}
<!-- boolean/checkbox input -->
@if (dataField.dataType === dataType.Boolean || dataField.dataType === dataType.Checkbox) {
<div class="full-width">
  @if (!showValueOnly) {
  <div class="editor-label">
    {{ dataField.name }}
  </div>
  }
  <div>
    <mat-checkbox [checked]="value" (change)="value = $event.checked.toString(); valueChangeHandler()">
    </mat-checkbox><br />&nbsp;
  </div>
</div>
}
<!-- Competency input (chips + search list) -->
@if (dataField.dataType === dataType.Competency) {
<span>
  @if (!showValueOnly) {
  <div class="competency-field">
    <label class="competency-label">{{ dataField.name }}</label>
    <mat-chip-set>
      @for (id of selectedCompetencies; track id) {
      <mat-chip (removed)="removeCompetency(id)" [matTooltip]="getCompetencyTooltip(id)" matTooltipPosition="above">
        {{ displayCompetency(id) }}
        @if (canEdit) {
        <button matChipRemove>
          <mat-icon fontIcon="mdi-close-circle-outline" style="transform: scale(0.85)"></mat-icon>
        </button>
        }
      </mat-chip>
      }
    </mat-chip-set>
    @if (canEdit) {
    <mat-form-field class="full-width">
      <mat-label>Select competencies</mat-label>
      <mat-select multiple [value]="selectedCompetencies" (selectionChange)="setDataValueArray($event.value)" (openedChange)="$event || (competencyFilter = '')" panelClass="competency-select-panel">
        <div class="competency-search-box">
          <mat-icon fontIcon="mdi-magnify"></mat-icon>
          <input [(ngModel)]="competencyFilter" placeholder="Search competencies..." (keydown)="$event.stopPropagation()">
          @if (competencyFilter) {
          <button mat-icon-button (click)="competencyFilter = ''; $event.stopPropagation()" title="Clear">
            <mat-icon fontIcon="mdi-close"></mat-icon>
          </button>
          }
        </div>
        <hr style="margin: 0; border-color: var(--mat-sys-outline-variant);">
        @for (option of filteredCompetencyOptions; track option.optionName) {
        <mat-option [value]="option.optionName">{{ option.optionName }} - {{ option.optionValue }}</mat-option>
        }
        @if (filteredCompetencyOptions.length === 0) {
        <div class="competency-no-results">No matching competencies</div>
        }
      </mat-select>
    </mat-form-field>
    }
  </div>
  }
  @if (showValueOnly) {
  <span>
    @for (id of selectedCompetencies; track id; let isLast = $last) {
      <span [matTooltip]="getCompetencyTooltip(id)" matTooltipPosition="above" style="white-space: nowrap;">{{ displayCompetency(id) }}</span>@if (!isLast) {<span>, </span>}
    }
  </span>
  }
</span>
}
<!-- dropdown input -->
@if (dataField.isChosenFromList && dataField.dataType !== dataType.Competency) {
<span>
  @if (!showValueOnly) {
  <mat-form-field class="full-width">
    <mat-label>{{ dataField.name }}</mat-label>
    <!-- single select -->
    @if (!dataField.isMultiSelect) {
    <mat-select placeholder="{{ dataField.name }}" [(ngModel)]="value" (ngModelChange)="valueChangeHandler()"
      [disabled]="!canEdit">
      @for (dataOption of dataField.dataOptions; track $index) {
      <mat-option [value]="dataOption.optionValue">
        {{ dataOption.optionName }}
      </mat-option>
      }
    </mat-select>
    }
    <!-- multi-select -->
    @if (dataField.isMultiSelect) {
    <mat-select multiple (click)="$event.stopPropagation()" placeholder="{{ dataField.name }}"
      (valueChange)="setDataValueArray($event)" [value]="getValueArray()" [disabled]="!canEdit">
      @for (dataOption of dataField.dataOptions; track $index) {
      <mat-option [value]="dataOption.optionValue">
        {{ dataOption.optionName }}
      </mat-option>
      }
    </mat-select>
    }
  </mat-form-field>
  }
  @if (showValueOnly) {
  <span>
    @if (!dataField.isMultiSelect) {
    {{ value }}
    } @else {
    @for (val of getValueArray(); track val) {
    {{ val }}<br>
    }
    }
  </span>
  }
</span>
}
}
<!-- editable display -->
